import React, { createContext, useContext, useRef, useState } from 'react';


type ModalTemplate = React.ReactNode | ((args: { _open: boolean; }) => React.ReactNode);

interface ModalOptions {
  content: ModalTemplate;
  [key: string]: any;
}

interface ModalState {
  content: ModalTemplate;
  open: boolean;
}

interface ModalContextProps {
  openModal: (openModalOptions: ModalOptions) => void;
  closeModal: () => void;
  registerModal: (id: string) => void;
  unregisterModal: (id: string) => void;
  getModalIndex: (id: string) => number;
  resetModalIndex: (id: string) => void;
}

interface ModalRendererProps {
  content: ModalTemplate;
  open: boolean;
}


const ModalContext = createContext<ModalContextProps | undefined>(undefined);

const ModalRenderer: React.FC<ModalRendererProps> = ({ content, open }) => {
  if (!content) return null;
  return <>{typeof content === 'function' ? content({ _open: open }) : content}</>;
};

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalStack, setModalStack] = useState<Record<string, number | null>>({});
  const modalIndex = useRef(0);
  const [modal, setModal] = useState<ModalState>({
    content: null,
    open: false,
  });

  const openModal = ({ content, ...options }: ModalOptions) => setModal({
    content,
    open: true,
  });

  const closeModal = () => setModal({
    content: null,
    open: false,
  });
  
  const registerModal = (id: string) => {
    setModalStack((prevStack) => ({...prevStack, [id]: null }));
  }

  const unregisterModal = (id: string) => {
    setModalStack((prevStack) => {
      const newStack = { ...prevStack };
      delete newStack[id];
      return newStack;
    });
  }

  const getModalIndex = (id: string) => {
    const index = modalIndex.current++;
    setModalStack((prevStack) => ({ ...prevStack, [id]: index }));
    return index;
  }

  const resetModalIndex = (id: string) => {
    modalIndex.current--;
    setModalStack((prevStack) => ({ ...prevStack, [id]: null }));
  }

  return (
    <ModalContext.Provider value={{ openModal, closeModal, getModalIndex, registerModal, unregisterModal, resetModalIndex }}>
      {children}
      <ModalRenderer content={modal.content} open={modal.open} />
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextProps => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};